<script lang="ts">
	import { Icon, Label, SupportedLanguages, Tooltip } from '@repo/commons/components'
	import {
		Modality,
		SupportedLanguage,
		type ProjectParameter
	} from '@semanticedge/persistence-service-noheaders-client/src'

	const MODALITY_LABELS: Record<Modality, string> = {
		[Modality.Chat]: 'Chat',
		[Modality.Voice]: 'Voice'
	}

	interface Props {
		imagePath: string
		secondaryText: string
		title: string
		description: string
		/** Gradient index 0–5 for the card image area (uses .gradient-0 … .gradient-5). */
		gradientIndex?: number
		chips?: string[]
		supportedLanguages?: SupportedLanguage[]
		projectParameters?: ProjectParameter[]
		expertise?: boolean
		showMoreIcon?: boolean
		onMoreIconClicked?: (title: string, e: MouseEvent) => void
		onCardSelected?: (title: string, e: MouseEvent) => void
	}

	let {
		imagePath,
		secondaryText,
		title,
		description,
		gradientIndex,
		chips = [],
		supportedLanguages = [],
		projectParameters = [],
		expertise = false,
		showMoreIcon = false,
		onMoreIconClicked,
		onCardSelected
	}: Props = $props()

	function handleMoreIconClick(event: MouseEvent) {
		event.stopPropagation()
		event.preventDefault()
		;(event.currentTarget as HTMLElement).blur()
		onMoreIconClicked?.(title, event)
	}

	function handleCardClick(event: MouseEvent) {
		onCardSelected?.(title, event)
	}

	function handleCardKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			handleCardClick(event as unknown as MouseEvent)
		}
	}

	function handleMoreIconKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			event.stopPropagation()
			handleMoreIconClick(event as unknown as MouseEvent)
		}
	}

	const gradientClass = $derived.by(() => `gradient-${gradientIndex}`)
</script>

<div
	class="card"
	role="button"
	tabindex="0"
	onclick={handleCardClick}
	onkeydown={handleCardKeydown}
>
	<div class="card-inner">
		{#if expertise}
			<div class="assistant-type-label">Expertise</div>
		{/if}
		{#if showMoreIcon}
			<div
				class="card-header"
				role="button"
				tabindex="-1"
				onclick={handleMoreIconClick}
				onkeydown={handleMoreIconKeydown}
			>
				<div class="more-icon-wrapper">
					<Icon name="more" size="md" class="text-[#002C4D80]" />
				</div>
			</div>
		{/if}
		<div class="card-image-wrapper {gradientClass}">
			{#if imagePath}
				<img src={imagePath} alt={title} class="card-image-img" />
			{/if}
		</div>
		<div class="card-content">
			<div class="card-text">
				<div class="header">
					<div class="flex items-center gap-2">
						<div class="secondary-text">{secondaryText ? secondaryText : ''}</div>
						{#if projectParameters?.length}
							{@const projectParametersHtml = projectParameters
								.map(
									(parameter) =>
										`<div class='parameter-name'>${parameter.name}: <span class='parameter-value'>${parameter.customType}</span></div>`
								)
								.join('')}
							<Tooltip
								html={`<h6 class='text-base font-semibold leading-tight tracking-widest mb-3'>Input/Output</h6><div class='tooltip-content'>${projectParametersHtml}</div>`}
								position="top"
								tooltipSize="large"
								offset={16}
							>
								<Icon name="info" size="sm" class="text-[#002C4D80]" />
							</Tooltip>
						{/if}
					</div>
					<div class="title">{title ?? ''}</div>
				</div>
				<div class="description">{description ? description : ''}</div>
			</div>
			<div class="card-footer inline-flex items-center gap-2">
				{#if chips && chips.length}
					<div class="chips-container inline-flex gap-1">
						{#each chips as chip}
							<Label label={MODALITY_LABELS[chip as Modality]} size="sm" variant="default" />
						{/each}
					</div>
				{/if}
				<SupportedLanguages {supportedLanguages} />
			</div>
		</div>
	</div>
</div>

<style lang="postcss">
	.card {
		@apply overflow-hidden w-full min-w-0 max-w-full h-[30rem] bg-white rounded-2xl border border-[#003A66]/10 shadow-[0_4px_8px_-2px_rgba(0,0,0,0.04),0_2px_4px_-2px_rgba(0,0,0,0.08)] flex flex-col justify-start items-center cursor-pointer outline-none;
		@apply focus-visible:ring-2 focus-visible:ring-[#007acc] focus-visible:ring-offset-2;
		@apply hover:shadow-[0_8px_16px_-4px_rgba(0,0,0,0.1),0_4px_8px_-4px_rgba(0,0,0,0.04)];
		@apply transition-shadow duration-300 ease-in-out;
	}
	.card-inner {
		@apply self-stretch min-w-0 h-[28rem] flex flex-col justify-start items-center relative;
	}
	.card-header {
		@apply absolute top-0 right-0 m-4 z-10 pointer-events-auto border-0 bg-transparent p-0 cursor-pointer outline-none focus:outline-none focus-visible:outline-none;
	}
	.more-icon-wrapper {
		@apply h-12 w-12 bg-[#edf1f2] rounded-lg flex justify-center items-center pointer-events-auto transition-transform transition-colors duration-200 ease-in-out hover:bg-[#d8e2e5] hover:scale-105;
	}
	.card-image-wrapper {
		@apply w-full flex justify-end items-end flex-shrink-0 bg-gray-200 overflow-hidden;
		height: 15.625rem;
	}
	.card-image-img {
		@apply w-[240px] mx-auto aspect-square object-cover;
		user-select: none;
	}
	.card-content {
		@apply self-stretch min-w-0 flex flex-col h-full justify-between gap-2 p-8;
	}
	.card-text {
		@apply self-stretch min-w-0 flex flex-col justify-start items-start gap-4;
		min-height: 8rem;
	}
	.header {
		@apply self-stretch min-w-0 flex flex-col justify-start items-start gap-1;
	}
	.secondary-text {
		@apply self-stretch min-w-0 text-[#001d33]/70 text-sm font-semibold uppercase leading-tight tracking-widest truncate;
	}
	.title {
		@apply self-stretch min-w-0 text-[#001526]/90 text-xl font-semibold leading-7 truncate;
	}
	.description {
		@apply self-stretch min-w-0 text-[#001d33]/70 text-base font-normal leading-normal;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
	.card-footer {
		@apply inline-flex flex-wrap justify-start items-center gap-1 min-w-0;
	}
	.chips-container {
		@apply min-w-0 flex-wrap;
	}
	.assistant-type-label {
		@apply absolute top-0 left-0 px-2.5 py-0.5 rounded-xl m-4 z-10 pointer-events-auto text-sm text-white bg-[#07343D];
	}

	.gradient-0 {
		background: linear-gradient(45deg, #c37df2 0%, #fff4f8 50%, #7094ff 100%);
	}

	.gradient-1 {
		background: linear-gradient(45deg, #7b40b1 0%, #fff4f8 50%, #a0def3 100%);
	}

	.gradient-2 {
		background: linear-gradient(45deg, #c299ff 0%, #fff4f8 50%, #7880f8 100%);
	}

	.gradient-3 {
		background: linear-gradient(45deg, #7094ff 0%, #fff4f8 50%, #c37df2 100%);
	}

	.gradient-4 {
		background: linear-gradient(45deg, #94e4ff 0%, #fff4f8 50%, #c78aff 100%);
	}

	.gradient-5 {
		background: linear-gradient(45deg, #b27de8 0%, #fff4f8 50%, #bbf2ed 100%);
	}

	.gradient-6 {
		background: linear-gradient(45deg, #daffd6 0%, #fff4f8 50%, #c08be4 100%);
	}

	.gradient-7 {
		background: linear-gradient(45deg, #c08be4 0%, #fff4f8 50%, #daffd6 100%);
	}

	.gradient-8 {
		background: linear-gradient(45deg, #c08be4 0%, #fff4f8 50%, #ffd6d6 100%);
	}

	.gradient-9 {
		background: linear-gradient(45deg, #ffd6d6 0%, #fff4f8 50%, #c08be4 100%);
	}

	.gradient-10 {
		background: linear-gradient(45deg, #9a8be4 0%, #fff4f8 50%, #e4d6ff 100%);
	}
</style>
